import { useState } from 'react';
import { apiPost, ApiResponse, ApiError } from '../../services/api.service';

interface CancellationData {
  cancellation_id: number;
  extra_id: number;
  event_id: number;
  reason: string;
  created_at: string;
}

interface CancelEventParams {
  extra_id: number;
  event_id: number;
  reason: string;
}

interface UseCancelEventReturn {
  cancelEvent: (params: CancelEventParams) => Promise<CancellationData | null>;
  loading: boolean;
  error: string | null;
  data: CancellationData | null;
}

/**
 * Custom hook to cancel an event
 * Creates a cancellation record and updates event status to 'cancelled'
 * 
 * @returns Object containing cancelEvent function, loading state, error state, and response data
 */
export function useCancelEvent(): UseCancelEventReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CancellationData | null>(null);

  const cancelEvent = async (params: CancelEventParams): Promise<CancellationData | null> => {
    try {
      setLoading(true);
      setError(null);

      const response: ApiResponse<CancellationData> = await apiPost<CancellationData>(
        '/cancellations',
        params
      );

      if (response.success && response.data) {
        setData(response.data);
        return response.data;
      } else {
        const errorMessage = response.error || 'Fehler beim Stornieren des Termins';
        setError(errorMessage);
        return null;
      }
    } catch (err) {
      let errorMessage = 'Ein unerwarteter Fehler ist beim Stornieren des Termins aufgetreten';
      
      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    cancelEvent,
    loading,
    error,
    data,
  };
}